/**
 * Widget Configuration Validation and Defaults
 */

import { z } from 'zod';
import type { WidgetConfig, ResolvedWidgetConfig, ColorConfig, FeaturesConfig, LayoutConfig, UserDetailsConfig } from '../types';

// Removed __API_BASE_URL__ - widget now REQUIRES apiBaseUrl in runtime config

/**
 * Zod schema for widget configuration validation
 */
const authConfigSchema = z.object({
  token: z.string().optional(),
  apiKey: z.string().optional(),
  ehrToken: z.string().optional(),
  clientId: z.string().optional(),
  facilityId: z.string().optional(),
  customHeaders: z.record(z.string()).optional(),
  tokenProvider: z.function().returns(z.promise(z.string())).optional(),
  parentOrigin: z.string().optional(),
}).optional();

const colorConfigSchema = z.object({
  primary: z.string().optional(),
  background: z.string().optional(),
  text: z.string().optional(),
  secondary: z.string().optional(),
  success: z.string().optional(),
  warning: z.string().optional(),
  destructive: z.string().optional(),
}).optional();

const featuresConfigSchema = z.object({
  search: z.boolean().optional(),
  filters: z.boolean().optional(),
  export: z.boolean().optional(),
  pagination: z.boolean().optional(),
  refreshInterval: z.number().min(0).optional(),
}).optional();

const layoutConfigSchema = z.object({
  height: z.union([z.string(), z.number()]).optional(),
  width: z.union([z.string(), z.number()]).optional(),
  showHeader: z.boolean().optional(),
  showFooter: z.boolean().optional(),
}).optional();

const userDetailsConfigSchema = z.object({
  userName: z.string().optional(),
}).catchall(z.string().optional()).optional();

export const widgetConfigSchema = z.object({
  apiBaseUrl: z.string().url(),
  auth: authConfigSchema,
  theme: z.enum(['light', 'dark']).optional(),
  colors: colorConfigSchema,
  features: featuresConfigSchema,
  layout: layoutConfigSchema,
  userDetails: userDetailsConfigSchema,
  multiFacilityMode: z.boolean().optional(),
  enableSessionKeepAlive: z.boolean().optional(),
  onSessionExpired: z.function().optional(),
  onLoad: z.function().optional(),
  onError: z.function().args(z.instanceof(Error)).optional(),
  onDataChange: z.function().args(z.unknown()).optional(),
  onRowClick: z.function().args(z.unknown()).optional(),
});

/**
 * Default color values
 */
export const DEFAULT_COLORS: Required<ColorConfig> = {
  primary: '#228189',
  background: '#E4F0F1',
  text: '#1a1a1a',
  secondary: '#6b7280',
  success: '#4CAF50',
  warning: '#FF9800',
  destructive: '#B02828',
};

/**
 * Default feature settings
 */
export const DEFAULT_FEATURES: Required<FeaturesConfig> = {
  search: true,
  filters: true,
  export: true,
  pagination: true,
  refreshInterval: 300000, // 5 minutes
};

/**
 * Default layout settings
 */
export const DEFAULT_LAYOUT: Required<LayoutConfig> = {
  height: '100%',
  width: '100%',
  showHeader: true,
  showFooter: true,
};

/**
 * Default user details
 */
export const DEFAULT_USER_DETAILS: Required<Pick<UserDetailsConfig, 'userName'>> = {
  userName: '',
};

/**
 * Validate widget configuration
 * @throws Error if configuration is invalid
 */
export function validateConfig(config: unknown): WidgetConfig {
  const result = widgetConfigSchema.safeParse(config);

  if (!result.success) {
    const errors = result.error.errors
      .map((e) => `${e.path.join('.')}: ${e.message}`)
      .join(', ');
    throw new Error(`Invalid widget configuration: ${errors}`);
  }

  return result.data as WidgetConfig;
}

/**
 * Merge user config with defaults to create resolved config
 * Note: apiBaseUrl is REQUIRED - must be provided in runtime config
 */
export function resolveConfig(config: WidgetConfig): ResolvedWidgetConfig {
  if (!config.apiBaseUrl) {
    throw new Error('MDS Widget: apiBaseUrl is required in widget configuration');
  }

  return {
    apiBaseUrl: config.apiBaseUrl,
    auth: config.auth,
    theme: config.theme ?? 'light',
    colors: {
      ...DEFAULT_COLORS,
      ...config.colors,
    },
    features: {
      ...DEFAULT_FEATURES,
      ...config.features,
    },
    layout: {
      ...DEFAULT_LAYOUT,
      ...config.layout,
    },
    userDetails: {
      ...DEFAULT_USER_DETAILS,
      ...config.userDetails,
    },
    multiFacilityMode: config.multiFacilityMode ?? false,
    enableSessionKeepAlive: config.enableSessionKeepAlive ?? true,
    onSessionExpired: config.onSessionExpired,
    onLoad: config.onLoad,
    onError: config.onError,
    onDataChange: config.onDataChange,
    onRowClick: config.onRowClick,
  };
}

/**
 * Validate and resolve configuration in one step
 */
export function processConfig(config: unknown): ResolvedWidgetConfig {
  const validated = validateConfig(config);
  return resolveConfig(validated);
}
